'use client';

import { useState, useEffect } from 'react';
import UserProvider from '@/context/userContext';
import CartProvider from '@/context/cartContext';
import WishlistProvider from '@/context/wishlistContext';
import CleverTapProvider from '@/context/clevertapContext';
import HeaderContextProvider from '@/context/headerContext';
import { LoaderProvider } from '@/context/loaderContext';
import LocationProvider from '@/context/locationContext';
import ListingProvider from '@/context/listingContext';
import { ScrollProvider } from '@/context/scrollContext';
import { pushGA4Event } from '@/utils/helper';
import { usePathname, useSearchParams } from 'next/navigation';

const GlobalProvider = ({ children }: { children: React.ReactNode }) => {
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // GA4 Acquisition: page_view on navigation
  useEffect(() => {
    if (!mounted) return;
    pushGA4Event('page_view', {
      page_url: window.location.href,
    });
  }, [mounted, pathname, searchParams]);

  // GA4 Acquisition: session_start + traffic_source (once per session)
  useEffect(() => {
    if (!mounted) return;

    try {
      const sessionStartSent = sessionStorage.getItem('ga4_session_start_sent');
      if (!sessionStartSent) {
        const url = new URL(window.location.href);
        const utm_source = url.searchParams.get('utm_source') || '';
        const utm_medium = url.searchParams.get('utm_medium') || '';
        const source =
          utm_source || (document.referrer ? 'referrer' : 'direct');
        const medium =
          utm_medium || (document.referrer ? 'referral' : '(none)');

        pushGA4Event('session_start', { source, medium });
        sessionStorage.setItem('ga4_session_start_sent', '1');
      }

      const trafficSourceSent = sessionStorage.getItem(
        'ga4_traffic_source_sent'
      );
      if (!trafficSourceSent) {
        const url = new URL(window.location.href);
        const utm_source = url.searchParams.get('utm_source') || '';
        const utm_medium = url.searchParams.get('utm_medium') || '';
        const utm_campaign = url.searchParams.get('utm_campaign') || '';
        const utm_term = url.searchParams.get('utm_term') || '';
        const utm_content = url.searchParams.get('utm_content') || '';

        // Only emit traffic_source when any utm param is present
        if (
          utm_source ||
          utm_medium ||
          utm_campaign ||
          utm_term ||
          utm_content
        ) {
          pushGA4Event('traffic_source', {
            utm_source,
            utm_medium,
            utm_campaign,
            utm_term,
            utm_content,
          });
          sessionStorage.setItem('ga4_traffic_source_sent', '1');
        }
      }
    } catch {
      // ignore
    }
  }, [mounted]);

  useEffect(() => {
    if (!mounted) return;

    const onError = (event: ErrorEvent) => {
      try {
        pushGA4Event('page_error', {
          error_code: String(
            event?.error?.message || event?.message || 'window_error'
          ),
        });
      } catch {
        // ignore
      }
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      try {
        const reason: any = event?.reason;
        pushGA4Event('page_error', {
          error_code: String(
            reason?.message || reason || 'unhandled_rejection'
          ),
        });
      } catch {
        // ignore
      }
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, [mounted]);

  if (!mounted) {
    return null;
  }

  return (
    <>
      <LoaderProvider>
        <CleverTapProvider>
          <LocationProvider>
            <UserProvider>
              <WishlistProvider>
                <CartProvider>
                  <ListingProvider>
                    <HeaderContextProvider>
                      <ScrollProvider>{children}</ScrollProvider>
                    </HeaderContextProvider>
                  </ListingProvider>
                </CartProvider>
              </WishlistProvider>
            </UserProvider>
          </LocationProvider>
        </CleverTapProvider>
      </LoaderProvider>
    </>
  );
};

export default GlobalProvider;
